import React, { useEffect, useState, useCallback, useMemo } from 'react';
import articlesData from './ArticlesData';
import '../styles/ActualitesMusee.css';

const NewsPage = () => {
    const [futureArticles, setFutureArticles] = useState([]);
    const [archivedArticles, setArchivedArticles] = useState([]);
    const [openYear, setOpenYear] = useState(null);
    const [pdfViewerVisible, setPdfViewerVisible] = useState(false);
    const [selectedPdfPath, setSelectedPdfPath] = useState(null);
    const [isMobile, setIsMobile] = useState(false);

    // --- LOGIQUE DE DATES ---
    const stringToDate = useCallback((dateStr) => {
        if (!dateStr) return new Date();
        const months = ["janvier", "février", "mars", "avril", "mai", "juin", "juillet", "août", "septembre", "octobre", "novembre", "décembre"];
        const parts = dateStr.toLowerCase().trim().split(' ');
        const year = parseInt(parts.find(p => p.length === 4 && !isNaN(p)), 10);
        const monthIndex = months.findIndex(m => parts.includes(m));
        const day = parseInt(parts.find(p => !isNaN(p) && p.length <= 2), 10);
        return new Date(year, monthIndex, day);
    }, []);

    const parseDatesRange = useCallback((dateRangeStr) => {
        const lowerStr = dateRangeStr.toLowerCase();
        if (lowerStr.includes(' au ')) {
            const segments = lowerStr.split(' au ');
            const startDate = stringToDate(segments[0]);
            const endDate = stringToDate(segments[1]);
            if (isNaN(startDate.getFullYear()) || startDate.getFullYear() < 2000) {
                startDate.setFullYear(endDate.getFullYear());
            }
            return { start: startDate, end: endDate };
        }
        const singleDate = stringToDate(dateRangeStr);
        return { start: singleDate, end: singleDate };
    }, [stringToDate]);

    // --- FILTRAGE & REPARTITION ---
    useEffect(() => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const processed = articlesData
            .filter(article => article.category === "musee")
            .map(article => ({
                ...article,
                parsed: parseDatesRange(article.date)
            }));

        const future = processed
            .filter(article => {
                const end = new Date(article.parsed.end);
                end.setHours(23, 59, 59, 999);
                return end >= today;
            })
            .sort((a, b) => a.parsed.start - b.parsed.start);

        const past = processed
            .filter(article => {
                const end = new Date(article.parsed.end);
                end.setHours(23, 59, 59, 999);
                return end < today && article.parsed.end.getFullYear() >= 2022;
            })
            .sort((a, b) => b.parsed.start - a.parsed.start);

        setFutureArticles(future);
        setArchivedArticles(past);

        const checkIsMobile = () => setIsMobile(window.innerWidth <= 768);
        checkIsMobile();
        window.addEventListener('resize', checkIsMobile);
        return () => window.removeEventListener('resize', checkIsMobile);
    }, [parseDatesRange]);

    const archivedByYear = useMemo(() => {
        return archivedArticles.reduce((acc, article) => {
            const year = article.parsed.end.getFullYear();
            if (!acc[year]) acc[year] = [];
            acc[year].push(article);
            return acc;
        }, {});
    }, [archivedArticles]);

    const sortedYears = useMemo(() => Object.keys(archivedByYear).sort((a, b) => b - a), [archivedByYear]);

    // --- NAVIGATION ---
    const scrollNewsGrid = (direction, elementId) => {
        const grid = document.getElementById(elementId);
        if (grid) {
            const scrollAmount = isMobile ? window.innerWidth * 0.8 : 324;
            grid.scrollBy({ left: direction * scrollAmount, behavior: 'smooth' });
        }
    };

    const openPdfViewer = (pdfPath) => {
        if (isMobile) {
            window.open(pdfPath, '_blank');
        } else {
            setSelectedPdfPath(pdfPath);
            setPdfViewerVisible(true);
            document.body.style.overflow = 'hidden';
        }
    };

    const closePdfViewer = () => {
        setPdfViewerVisible(false);
        document.body.style.overflow = 'unset';
    };

    const toggleAccordion = (year) => setOpenYear(openYear === year ? null : year);

    return (
        <main>
            <section className="hero fade-in">
                <h1>Les Actualités du Musée</h1>
                <p>Découvrez les dernières actualités du Musée des Troupes de Marine</p>
            </section>

            <section className="content-section fade-in">
                <h2>Introduction</h2>
                <p>Retrouvez ici nos événements en cours et nos archives depuis 2022.</p>
            </section>

            {/* SECTION À VENIR */}
            {futureArticles.length > 0 && (
                <section className="news-section fade-in">
                    <h2>Les Actualités à venir</h2>
                    <div className="news-grid-container">
                        <button className="arrow left-arrow" onClick={() => scrollNewsGrid(-1, 'main-grid')}>&#10094;</button>
                        <div className="news-grid" id="main-grid">
                            {futureArticles.map(article => (
                                <div key={article.id} className="news-item">
                                    <img src={article.image} alt={article.title} />
                                    <h3>{article.title}</h3>
                                    <p><strong>{article.date}</strong></p>
                                    <p>{article.description}</p>
                                    <button className="btn btn-primary" onClick={() => openPdfViewer(article.pdfPath)}>Lire l'article</button>
                                </div>
                            ))}
                        </div>
                        <button className="arrow right-arrow" onClick={() => scrollNewsGrid(1, 'main-grid')}>&#10095;</button>
                    </div>
                </section>
            )}

            {/* SECTION ARCHIVES */}
            <section className="content-section fade-in">
                <h2>Les archives</h2>
                <div id="archived-articles">
                    {sortedYears.map(year => (
                        <div key={year} className={`accordion-item ${openYear === year ? 'active' : ''}`}>
                            <div className={`accordion-header ${openYear === year ? 'active' : ''}`} onClick={() => toggleAccordion(year)}>
                                <h3>{year}</h3>
                            </div>
                            
                            <div className={`accordion-content ${openYear === year ? 'show' : ''}`}>
                                <div className="news-grid-container">
                                    <button className="arrow left-arrow" onClick={() => scrollNewsGrid(-1, `grid-${year}`)}>&#10094;</button>
                                    <div className="news-grid" id={`grid-${year}`}>
                                        {archivedByYear[year].map(article => (
                                            <div key={article.id} className="news-item">
                                                <img src={article.image} alt={article.title} />
                                                <h3>{article.title}</h3>
                                                <p>{article.date}</p>
                                                <p>{article.description}</p>
                                                <button className="btn btn-primary" onClick={() => openPdfViewer(article.pdfPath)}>Lire l'article</button>
                                            </div>
                                        ))}
                                    </div>
                                    <button className="arrow right-arrow" onClick={() => scrollNewsGrid(1, `grid-${year}`)}>&#10095;</button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* PDF VIEWER */}
            {!isMobile && pdfViewerVisible && selectedPdfPath && (
                <div className="pdf-viewer-overlay" onClick={closePdfViewer}>
                    <div className="pdf-viewer-container" onClick={(e) => e.stopPropagation()}>
                        <div className="pdf-viewer-header">
                            <button className="pdf-download-btn" onClick={() => window.open(selectedPdfPath)}>Télécharger</button>
                            <button className="close-pdf" onClick={closePdfViewer}>&times;</button>
                        </div>
                        <div className="pdf-content">
                            <iframe src={selectedPdfPath} className="pdf-viewer" title="PDF" key={selectedPdfPath}></iframe>
                        </div>
                    </div>
                </div>
            )}
        </main>
    );
};

export default NewsPage;